import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatDate } from '@/lib/utils'
import { CheckSquare, Square, Trash2, Edit3, Check, X, Plus, Phone, Mail, ClipboardList, Clock } from 'lucide-react'
import { toast } from 'sonner'
import UserPicker from '@/pages/team/UserPicker'

/**
 * TasksThread — polymorphic sales tasks on a deal / contact / company.
 *
 * Mirrors NoteThread (the entity_notes thread) over the `sales_tasks` table:
 * query by entity_type+entity_id, a create form, an assignee-aware list with
 * complete-checkbox + edit/delete. RLS enforces tenant isolation + the
 * creator-or-assignee update rule; the component just talks to the table.
 *
 * variant='full' (a record-detail tab) shows the create card always open;
 * 'compact' (the deal sidebar) collapses it behind a "+ Add task" affordance.
 * sales_tasks is not in the generated types yet — the client is cast.
 */

type EntityType = 'deal' | 'contact' | 'company'
type TaskType = 'todo' | 'call' | 'email'

interface Props {
  entityType: EntityType
  entityId: string
  variant?: 'full' | 'compact'
}

interface TaskRow {
  id: string
  title: string
  task_type: string
  status: string
  due_date: string | null
  remind_at: string | null
  assigned_to: string | null
  created_by: string
  completed_at: string | null
  created_at: string
}

interface MemberRow { id: string; full_name: string | null }

const queryKey = (type: string, id: string) => ['sales-tasks', type, id] as const

const TYPE_META: Record<string, { label: string; Icon: React.ComponentType<{ style?: React.CSSProperties }> }> = {
  todo: { label: 'To-do', Icon: ClipboardList },
  call: { label: 'Call', Icon: Phone },
  email: { label: 'Email', Icon: Mail },
}
const TASK_TYPES: TaskType[] = ['todo', 'call', 'email']

const field: React.CSSProperties = {
  padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)',
  borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box',
}

export default function TasksThread({ entityType, entityId, variant = 'full' }: Props) {
  const queryClient = useQueryClient()
  const { profile, workspace } = useAuth()
  const meId = profile?.id ?? null
  const workspaceId = workspace?.id ?? null
  const isAdmin = !!profile && (profile.is_owner || profile.role === 'admin')
  const compact = variant === 'compact'

  // create-form state
  const [showCreate, setShowCreate] = useState(!compact)
  const [title, setTitle] = useState('')
  const [taskType, setTaskType] = useState<TaskType>('todo')
  const [dueDate, setDueDate] = useState('')
  const [remindAt, setRemindAt] = useState('')
  const [assignedTo, setAssignedTo] = useState<string | null>(null)
  const [showAssignPicker, setShowAssignPicker] = useState(false)

  // edit state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editType, setEditType] = useState<TaskType>('todo')
  const [editDue, setEditDue] = useState('')

  const { data: tasks = [], isLoading } = useQuery<TaskRow[]>({
    queryKey: queryKey(entityType, entityId),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('sales_tasks')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as TaskRow[]
    },
    enabled: !!entityId,
  })

  // resolve assignee names (mirror NoteThread's author resolution)
  const assigneeIds = useMemo(
    () => Array.from(new Set(tasks.map(t => t.assigned_to).filter((v): v is string => !!v))),
    [tasks],
  )
  const { data: members = [] } = useQuery<MemberRow[]>({
    queryKey: ['sales-tasks-assignees', assigneeIds.sort().join(',')],
    queryFn: async () => {
      if (assigneeIds.length === 0) return []
      const { data, error } = await supabase.from('profiles').select('id, full_name').in('id', assigneeIds)
      if (error) throw error
      return (data ?? []) as MemberRow[]
    },
    enabled: assigneeIds.length > 0,
    staleTime: 60_000,
  })
  const nameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of members) m.set(a.id, a.full_name || 'Teammate')
    if (meId && profile?.full_name) m.set(meId, profile.full_name)
    return m
  }, [members, meId, profile?.full_name])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKey(entityType, entityId) })

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !meId) throw new Error('Not signed in')
      const { error } = await (supabase as any).from('sales_tasks').insert({
        workspace_id: workspaceId,
        entity_type: entityType,
        entity_id: entityId,
        title: title.trim(),
        task_type: taskType,
        status: 'open',
        due_date: dueDate || null,
        remind_at: remindAt ? new Date(remindAt).toISOString() : null,
        assigned_to: assignedTo,
        created_by: meId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setTitle(''); setTaskType('todo'); setDueDate(''); setRemindAt(''); setAssignedTo(null)
      setShowAssignPicker(false)
      if (compact) setShowCreate(false)
      invalidate()
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to add task'),
  })

  const toggleComplete = useMutation({
    mutationFn: async (t: TaskRow) => {
      const done = t.status !== 'done'
      const { error } = await (supabase as any).from('sales_tasks')
        .update({ status: done ? 'done' : 'open', completed_at: done ? new Date().toISOString() : null })
        .eq('id', t.id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || 'Failed to update task'),
  })

  const updateMutation = useMutation({
    mutationFn: async (vars: { id: string; title: string; task_type: string; due_date: string | null }) => {
      const { error } = await (supabase as any).from('sales_tasks')
        .update({ title: vars.title, task_type: vars.task_type, due_date: vars.due_date })
        .eq('id', vars.id)
      if (error) throw error
    },
    onSuccess: () => { setEditingId(null); invalidate() },
    onError: (e: any) => toast.error(e?.message || 'Failed to update task'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('sales_tasks').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || 'Failed to delete task'),
  })

  const submit = () => { if (title.trim() && !createMutation.isPending) createMutation.mutate() }
  const startEdit = (t: TaskRow) => {
    setEditingId(t.id); setEditTitle(t.title); setEditType((t.task_type as TaskType) || 'todo')
    setEditDue(t.due_date ? t.due_date.slice(0, 10) : '')
  }
  const saveEdit = () => {
    if (!editingId || !editTitle.trim()) return
    updateMutation.mutate({ id: editingId, title: editTitle.trim(), task_type: editType, due_date: editDue || null })
  }

  const assigneeLabel = assignedTo ? (nameById.get(assignedTo) || 'Assigned') : 'Unassigned'

  // ── the create form (shared by both variants) ──────────────────────────────
  const createForm = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: compact ? 10 : 14 }}>
      <input value={title} onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
        placeholder="Task title…" style={{ ...field, fontWeight: 600 }} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7 }}>
          {TASK_TYPES.map(t => {
            const active = taskType === t
            return (
              <button key={t} type="button" onClick={() => setTaskType(t)} aria-pressed={active}
                style={{ padding: '3px 9px', fontSize: 11, fontWeight: 700, borderRadius: 4, cursor: 'pointer', background: active ? 'var(--accent)' : 'transparent', color: active ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: 'none' }}>
                {TYPE_META[t].label}
              </button>
            )
          })}
        </div>
        <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} title="Due date"
          style={{ ...field, width: 140, colorScheme: 'dark' }} />
        <input type="datetime-local" value={remindAt} onChange={e => setRemindAt(e.target.value)} title="Remind me"
          style={{ ...field, width: 190, colorScheme: 'dark' }} />
      </div>
      {/* assignee */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Assignee:</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: assignedTo ? 'var(--ink-primary)' : 'var(--ink-tertiary)' }}>{assigneeLabel}</span>
        <button type="button" onClick={() => setShowAssignPicker(s => !s)}
          style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {showAssignPicker ? 'Close' : 'Pick'}
        </button>
        {meId && assignedTo !== meId && (
          <button type="button" onClick={() => { setAssignedTo(meId); setShowAssignPicker(false) }}
            style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            Assign to me
          </button>
        )}
        {assignedTo && (
          <button type="button" onClick={() => setAssignedTo(null)} aria-label="Clear assignee"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex' }}>
            <X style={{ width: 12, height: 12 }} />
          </button>
        )}
        {showAssignPicker && workspaceId && (
          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: 4, width: 260, padding: 10, background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-popover-mid)' }}>
            <UserPicker workspaceId={workspaceId} excludeUserIds={[]} mode="single"
              selectedIds={assignedTo ? [assignedTo] : []}
              onChange={ids => { setAssignedTo(ids[0] ?? null); setShowAssignPicker(false) }} />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        {compact && (
          <button type="button" onClick={() => setShowCreate(false)}
            style={{ padding: '6px 10px', background: 'var(--border-subtle)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600 }}>Cancel</button>
        )}
        <button type="button" onClick={submit} disabled={!title.trim() || createMutation.isPending}
          style={{ padding: '6px 12px', background: 'var(--accent)', border: 'none', borderRadius: 7, cursor: !title.trim() ? 'not-allowed' : 'pointer', color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, opacity: !title.trim() || createMutation.isPending ? 0.5 : 1 }}>
          {createMutation.isPending ? 'Adding…' : 'Add task'}
        </button>
      </div>
    </div>
  )

  const now = new Date()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 8 : 16 }}>
      {compact && !showCreate && (
        <button type="button" onClick={() => setShowCreate(true)}
          style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'none', border: '1px dashed var(--border-default)', borderRadius: 7, color: 'var(--ink-tertiary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          <Plus style={{ width: 12, height: 12 }} /> Add task
        </button>
      )}
      {showCreate && createForm}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: compact ? 12 : 24, color: 'var(--ink-tertiary)', fontSize: 12 }}>Loading tasks…</div>
      ) : tasks.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, textAlign: compact ? 'left' : 'center', padding: compact ? 0 : '24px 0' }}>No tasks</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 10 }}>
          <AnimatePresence initial={false}>
            {tasks.map(t => {
              const done = t.status === 'done'
              const meta = TYPE_META[t.task_type] || TYPE_META.todo
              const Icon = meta.Icon
              const overdue = !done && t.due_date && new Date(t.due_date) < now
              const canEdit = !!meId && (t.created_by === meId || t.assigned_to === meId || isAdmin)
              const isEditing = editingId === t.id
              return (
                <motion.div key={t.id}
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.14 }}
                  style={{ display: 'flex', gap: 10, padding: compact ? 10 : 12, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, alignItems: 'flex-start' }}>
                  <button type="button" onClick={() => toggleComplete.mutate(t)} aria-label={done ? 'Reopen task' : 'Complete task'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: done ? 'var(--positive)' : 'var(--ink-tertiary)', display: 'flex', marginTop: 1 }}>
                    {done ? <CheckSquare style={{ width: 16, height: 16 }} /> : <Square style={{ width: 16, height: 16 }} />}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <input autoFocus value={editTitle} onChange={e => setEditTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null) }}
                          style={{ ...field, fontWeight: 600 }} />
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7 }}>
                            {TASK_TYPES.map(tt => (
                              <button key={tt} type="button" onClick={() => setEditType(tt)}
                                style={{ padding: '3px 9px', fontSize: 11, fontWeight: 700, borderRadius: 4, cursor: 'pointer', background: editType === tt ? 'var(--accent)' : 'transparent', color: editType === tt ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: 'none' }}>
                                {TYPE_META[tt].label}
                              </button>
                            ))}
                          </div>
                          <input type="date" value={editDue} onChange={e => setEditDue(e.target.value)} style={{ ...field, width: 140, colorScheme: 'dark' }} />
                          <button type="button" onClick={saveEdit} disabled={!editTitle.trim()}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 9px', background: 'var(--accent)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--accent-ink)', fontSize: 12, fontWeight: 600, opacity: !editTitle.trim() ? 0.5 : 1 }}>
                            <Check style={{ width: 12, height: 12 }} /> Save
                          </button>
                          <button type="button" onClick={() => setEditingId(null)}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 9px', background: 'var(--border-subtle)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600 }}>
                            <X style={{ width: 12, height: 12 }} /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: done ? 'var(--ink-tertiary)' : 'var(--ink-primary)', textDecoration: done ? 'line-through' : 'none' }}>{t.title}</span>
                          {canEdit && (
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              <button onClick={() => startEdit(t)} aria-label="Edit task"
                                style={{ padding: 3, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', borderRadius: 6 }}>
                                <Edit3 style={{ width: 12, height: 12 }} />
                              </button>
                              <button onClick={() => { if (window.confirm('Delete this task?')) deleteMutation.mutate(t.id) }} aria-label="Delete task"
                                style={{ padding: 3, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', borderRadius: 6 }}
                                onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)' }}
                                onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)' }}>
                                <Trash2 style={{ width: 12, height: 12 }} />
                              </button>
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap', fontSize: 11, color: 'var(--ink-tertiary)' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon style={{ width: 11, height: 11 }} /> {meta.label}</span>
                          {t.due_date && <span style={{ color: overdue ? 'var(--danger)' : 'var(--ink-tertiary)', fontWeight: overdue ? 700 : 400 }}>Due {formatDate(t.due_date, 'MMM d')}</span>}
                          {t.assigned_to && <span>{nameById.get(t.assigned_to) || 'Assigned'}</span>}
                          {t.remind_at && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Clock style={{ width: 10, height: 10 }} /> {formatDate(t.remind_at, 'MMM d, HH:mm')}</span>}
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
